import { useMemo, useState, createContext, useContext, useRef, useEffect } from "react";
import { InRow, PageTitle } from "common/Grid";
import { Form, FormField as BaseFormField, PrimaryButton, SecondaryButton } from "common/Forms";
import { FloatInput, SelectInput, TextAreaInput, TextInput, ToggleButton } from "common/Forms/inputs";
import { Grid, GridCell } from "common/Grid/Grid";
import { validationRuleFactory } from "common/Forms/validation";
import { IoInformationCircleOutline } from "react-icons/io5";
import { toast } from "react-toastify";
import { countries, languageCodeOptions, languageKeyOptions, languageNameOptions } from "Constant/Constant";
import { parseRequestSiteConfigErrors } from "../../sales-leads/Common/requestSiteConfig.schema";
import { getCountries, getCountryCallingCode } from "react-phone-number-input";
import en from "react-phone-number-input/locale/en";

const HEX_COLOR_REGEX = /^#([0-9a-fA-F]{3}|[0-9a-fA-F]{6})$/;
const MASK_DISPLAY = "*******";

const FormErrorContext = createContext({});

// Always shows a fixed mask, even when there's no stored value yet; a single click/focus
// clears the mask and turns the field into a normal editable input, so a new value can be
// typed without ever having rendered the old one into the DOM.
const MaskedTextInput = ({ id, name, value, placeholder, onValidation, onChange, validationRules, type = "text" }) => {
  const [revealed, setRevealed] = useState(false);

  // TextInput doesn't forward refs, so the masked <input> swaps out for a different DOM
  // node when revealed. Focus it by id once that node exists — requestAnimationFrame runs
  // after React commits/paints, so (unlike a plain useEffect) it can't lose a focus race.
  const reveal = () => {
    if (revealed) return;
    setRevealed(true);
    onChange(value, "");
    requestAnimationFrame(() => document.getElementById(id)?.focus());
  };

  if (!revealed) {
    return (
      <input
        id={id}
        name={name || id}
        type="text"
        readOnly
        value={MASK_DISPLAY}
        placeholder={placeholder}
        onFocus={reveal}
        onMouseDown={reveal}
        className="h-[28px] w-full min-w-44 rounded p-2 py-6 box-border text-[16px] font-medium leading-5 focus:outline-none border-2 cursor-text"
      />
    );
  }

  return (
    <TextInput
      id={id}
      name={name}
      type={type}
      isCopyable={false}
      value={value}
      placeholder={placeholder}
      onValidation={onValidation}
      onChange={onChange}
      validationRules={validationRules}
    />
  );
};

const FormField = (props) => {
  const errors = useContext(FormErrorContext);
  const errorKey = Object.keys(errors).find(
    (k) =>
      k.split(".").pop() === props.id ||
      k.split(".").pop().toLowerCase() === props.id?.toLowerCase() ||
      k === props.id,
  );
  const { inputFactory, ...rest } = props;
  const wrappedInputFactory = inputFactory
    ? (inputId, name, onValidation) => (
      <div className="text-black">{inputFactory(inputId, name, onValidation)}</div>
    )
    : undefined;
  return (
    <BaseFormField
      {...rest}
      inputFactory={wrappedInputFactory}
      errorMessage={errorKey ? errors[errorKey] : undefined}
    />
  );
};

export const MainForm = ({ userInput, setUserInput, onSubmit, onCancel, apiErrors }) => {
  const [isSubmitted, setIsSubmitted] = useState(false);
  const selectedProvider = userInput?.paymentProvider || "stripe";

  // ── Country prefix dropdown state ──
  const [prefixOpen, setPrefixOpen] = useState(false);
  const [prefixSearch, setPrefixSearch] = useState("");
  const prefixRef = useRef(null);
  const prefixSearchRef = useRef(null);

  // Close on outside click
  useEffect(() => {
    if (!prefixOpen) return;
    const handler = (e) => {
      if (prefixRef.current && !prefixRef.current.contains(e.target)) {
        setPrefixOpen(false);
        setPrefixSearch("");
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [prefixOpen]);

  // Auto-focus search when dropdown opens
  useEffect(() => {
    if (prefixOpen) {
      requestAnimationFrame(() => prefixSearchRef.current?.focus());
    }
  }, [prefixOpen]);

  const getFlagEmoji = (countryCode) =>
    countryCode
      .toUpperCase()
      .split("")
      .map((char) => String.fromCodePoint(127397 + char.charCodeAt(0)))
      .join("");

  const countryOptions = useMemo(
    () =>
      getCountries()
        .map((country) => ({
          code: country,
          name: en[country] || country,
          dialCode: `+${getCountryCallingCode(country)}`,
          flag: getFlagEmoji(country),
        }))
        .sort((a, b) => a.name.localeCompare(b.name)),
    [],
  );

  const filteredPrefixOptions = useMemo(() => {
    if (!prefixSearch.trim()) return countryOptions;
    const q = prefixSearch.toLowerCase();
    return countryOptions.filter(
      (c) =>
        c.name.toLowerCase().includes(q) ||
        c.code.toLowerCase().includes(q) ||
        c.dialCode.includes(q),
    );
  }, [prefixSearch, countryOptions]);

  const selectedCountryCode = useMemo(() => {
    if (!userInput?.phonePrefix) return "";
    const match = countryOptions.find((c) => c.dialCode === userInput?.phonePrefix);
    return match ? match.code : "";
  }, [userInput?.phonePrefix, countryOptions]);

  const textInputBoxClass = ["text_input_box"];

  const formErrors = useMemo(() => {
    const errors = isSubmitted
      ? parseRequestSiteConfigErrors(userInput, selectedProvider, !!userInput?.franchiseid) || {}
      : {};
    delete errors.favicon;
    // Sensitive fields skip client-side required checks (validated server-side instead),
    // so surface any invalid keys the backend rejected the same way a required error would show.
    return { ...errors, ...(apiErrors || {}) };
  }, [userInput, selectedProvider, isSubmitted, apiErrors]);

  const [validationResult, setValidationResult] = useState({});
  const isValid = Object.values(validationResult).every((x) => x);

  // Each field must use its own useMemo for validation rules
  const siteNameValidationRules = useMemo(
    () => [validationRuleFactory.required("Site name required")],
    [],
  );

  const colorCodeValidationRules = useMemo(
    () => [validationRuleFactory.pattern(HEX_COLOR_REGEX, "Use HEX like #000000")],
    [],
  );

  const onValidationWrapper = (fieldName, onValidation) => {
    return (isValid, failedResults) => {
      setValidationResult((prev) => ({ ...prev, [fieldName]: isValid }));
      if (onValidation) {
        onValidation(isValid, failedResults);
      }
    };
  };

  const handleSubmit = () => {
    setIsSubmitted(true);
    const errors = parseRequestSiteConfigErrors(userInput, selectedProvider, !!userInput?.franchiseid) || {};
    delete errors.favicon;
    if (Object.keys(errors).length > 0) {
      toast.error("Please fill all required fields");
      return;
    }
    onSubmit(userInput);
  };

  const handleCountryChange = (configKey, n, selector) => {
    const selected = n ? selector(n).toLowerCase() : "";
    const meta = countries.find((x) => x.value === selected) || {};

    setUserInput((prev) => ({
      ...prev,
      [configKey]: {
        ...prev[configKey],
        country: selected,
        currency: meta.currency,
      },
    }));
  };

  // useEffect(() => {
  //   window.scrollTo(0, 0);
  // }, []);

  const handlePosToggle = (posType, checked) => {
    if (checked) {
      setUserInput((prev) => ({
        ...prev,
        squareSync: false,
        triniteqPos: false,
        tevalisPos: false,
        [posType]: true,
        // Square POS requires the Square payment provider, so lock it in automatically.
        ...(posType === "squareSync" ? { paymentProvider: "square" } : {}),
      }));
    } else {
      setUserInput((prev) => ({ ...prev, [posType]: false }));
    }
  };

  return (
    <FormErrorContext.Provider value={formErrors}>
      <Form>
        <Grid columns={4} rowGap={1.5} columnGap={2} classNames={["!gap-x-3 !gap-y-3"]}>
          {/* Main Information */}
          <GridCell columnSpan={4}>
            <PageTitle title="Main Information" />
          </GridCell>

          <GridCell columnSpan={2}>
            <FormField
              id="siteName"
              classNames={textInputBoxClass}
              label="Site Name"
              required={true}
              inputFactory={(id, name, onValidation) => (
                <TextInput
                  id={id}
                  name={name}
                  isCopyable={true}
                  value={userInput?.appConfig?.SiteName}
                  placeholder="Enter Site Name"
                  onValidation={onValidationWrapper(id, onValidation)}
                  onChange={(_, n) =>
                    setUserInput((prev) => ({
                      ...prev,
                      appConfig: { ...prev.appConfig, SiteName: n },
                    }))
                  }
                  validationRules={siteNameValidationRules}
                />
              )}
            />
          </GridCell>

          {!!userInput?.franchiseid && (
            <GridCell columnSpan={2}>
              <FormField
                id="companyName"
                classNames={textInputBoxClass}
                label="Company Name"
                required={true}
                inputFactory={(id, name, onValidation) => (
                  <TextInput
                    id={id}
                    name={name}
                    isCopyable={true}
                    value={userInput?.appConfig?.companyName}
                    placeholder="Enter Company Name"
                    onValidation={onValidation}
                    onChange={(_, n) =>
                      setUserInput((prev) => ({
                        ...prev,
                        appConfig: { ...prev.appConfig, companyName: n },
                      }))
                    }
                  />
                )}
              />
            </GridCell>
          )}

          <GridCell columnSpan={2}>
            <FormField
              id="mainSiteId"
              classNames={textInputBoxClass}
              label="Site Id"
              inputFactory={(id, name, onValidation) => (
                <TextInput
                  id={id}
                  name={name}
                  isCopyable={true}
                  disabled={true}
                  type="number"
                  min={0}
                  value={String(userInput?.siteid)}
                  placeholder="Enter Site Numeric Id"
                  onValidation={onValidation}
                  onChange={(_, n) =>
                    setUserInput((prev) => ({ ...prev, siteid: parseFloat(n) || 0 }))
                  }
                />
              )}
            />
          </GridCell>

          <GridCell columnSpan={2}>
            <FormField
              id="timeZone"
              classNames={textInputBoxClass}
              label="Time Zone"
              required={true}
              inputFactory={(id, name, onValidation) => (
                <TextInput
                  id={id}
                  name={name}
                  isCopyable={true}
                  disabled={true}
                  value={userInput?.appConfig?.timeZone}
                  placeholder="Enter Time Zone"
                  onValidation={onValidation}
                  onChange={(_, n) =>
                    setUserInput((prev) => ({
                      ...prev,
                      appConfig: { ...prev.appConfig, timeZone: n },
                    }))
                  }
                />
              )}
            />
          </GridCell>

          <GridCell columnSpan={2}>
            <FormField
              id="phonePrefix"
              label="Phone Prefix"
              inputFactory={(id, name) => {
                const selected = selectedCountryCode
                  ? countryOptions.find((c) => c.code === selectedCountryCode)
                  : null;
                return (
                  <div
                    style={{ display: "flex", alignItems: "stretch", width: "100%", position: "relative" }}
                    ref={prefixRef}
                  >
                    <button
                      type="button"
                      id="phonePrefixBtn"
                      onClick={() => { setPrefixOpen((o) => !o); setPrefixSearch(""); }}
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "4px",
                        flexShrink: 0,
                        height: "39px",
                        padding: "0 10px",
                        border: "2px solid #e2e8f0",
                        borderRadius: "4px",
                        background: "#f8fafc",
                        cursor: "pointer",
                        fontSize: "14px",
                        fontWeight: 500,
                        color: "#374151",
                        whiteSpace: "nowrap",
                        width: "100%",
                        justifyContent: "space-between",
                        outline: "none",
                        userSelect: "none",
                      }}
                    >
                      <div style={{ display: "flex", alignItems: "center", gap: "4px" }}>
                        <span style={{ fontSize: "18px", lineHeight: 1 }}>{selected ? selected.flag : "🌐"}</span>
                        <span style={{ fontSize: "13px" }}>{selected ? selected.dialCode : "e.g. +61"}</span>
                      </div>
                      <span style={{ fontSize: "9px", color: "#94a3b8", marginLeft: "2px" }}>{prefixOpen ? "▲" : "▼"}</span>
                    </button>

                    {prefixOpen && (
                      <div
                        style={{
                          position: "absolute",
                          top: "calc(100% + 4px)",
                          left: 0,
                          zIndex: 1000,
                          background: "#fff",
                          border: "1.5px solid #e2e8f0",
                          borderRadius: "8px",
                          boxShadow: "0 8px 24px rgba(0,0,0,0.12)",
                          width: "300px",
                          display: "flex",
                          flexDirection: "column",
                          overflow: "hidden",
                        }}
                      >
                        <div style={{ padding: "8px", borderBottom: "1px solid #f1f5f9" }}>
                          <input
                            ref={prefixSearchRef}
                            type="text"
                            placeholder="Search country or code…"
                            value={prefixSearch}
                            onChange={(e) => setPrefixSearch(e.target.value)}
                            onKeyDown={(e) => e.stopPropagation()}
                            style={{
                              width: "100%",
                              padding: "6px 10px",
                              border: "1.5px solid #e2e8f0",
                              borderRadius: "6px",
                              fontSize: "13px",
                              outline: "none",
                              boxSizing: "border-box",
                            }}
                          />
                        </div>
                        <div style={{ maxHeight: "220px", overflowY: "auto" }}>
                          {filteredPrefixOptions.length === 0 ? (
                            <div style={{ padding: "12px", textAlign: "center", color: "#94a3b8", fontSize: "13px" }}>
                              No country found
                            </div>
                          ) : (
                            filteredPrefixOptions.map((c) => (
                              <button
                                key={c.code}
                                type="button"
                                onClick={() => {
                                  setUserInput((prev) => ({
                                    ...prev,
                                    phonePrefix: c.dialCode,
                                  }));
                                  setPrefixOpen(false);
                                  setPrefixSearch("");
                                }}
                                style={{
                                  display: "flex",
                                  alignItems: "center",
                                  gap: "8px",
                                  width: "100%",
                                  padding: "8px 12px",
                                  border: "none",
                                  background: selectedCountryCode === c.code ? "#f0f9ff" : "transparent",
                                  cursor: "pointer",
                                  fontSize: "13px",
                                  textAlign: "left",
                                  color: "#374151",
                                }}
                                onMouseEnter={(e) => {
                                  if (selectedCountryCode !== c.code) e.currentTarget.style.background = "#f8fafc";
                                }}
                                onMouseLeave={(e) => {
                                  e.currentTarget.style.background =
                                    selectedCountryCode === c.code ? "#f0f9ff" : "transparent";
                                }}
                              >
                                <span style={{ fontSize: "18px", lineHeight: 1, flexShrink: 0 }}>{c.flag}</span>
                                <span style={{ flex: 1, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                                  {c.name}
                                </span>
                                <span style={{ color: "#64748b", fontWeight: 600, flexShrink: 0 }}>{c.dialCode}</span>
                              </button>
                            ))
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                );
              }}
            />
          </GridCell>

          <GridCell columnSpan={userInput?.franchiseid ? 2 : 4}>
            <FormField
              id="locale"
              classNames={textInputBoxClass}
              label="Locale"
              inputFactory={(id, name, onValidation) => (
                <TextInput
                  id={id}
                  name={name}
                  isCopyable={true}
                  value={userInput?.locale}
                  placeholder="Enter locale (e.g., en-au)"
                  onValidation={onValidation}
                  onChange={(_, n) => setUserInput((prev) => ({ ...prev, locale: n }))}
                />
              )}
            />
          </GridCell>

          {!!userInput?.franchiseid && (
          <GridCell columnSpan={4}>
            <FormField
              id="franchiseid"
              classNames={textInputBoxClass}
              label="Franchise Id"
              inputFactory={(id, name, onValidation) => (
                <TextInput
                  id={id}
                  name={name}
                  isCopyable={true}
                  disabled
                  value={userInput?.franchiseid}
                  placeholder="Enter Franchise Id"
                  onValidation={onValidation}
                  onChange={(_, n) => setUserInput((prev) => ({ ...prev, franchiseid: n }))}
                />
              )}
            />
          </GridCell>
          )}

          <GridCell columnSpan={!!userInput?.franchiseid ? 2 : 4}>
            <FormField
              id="appIcon"
              classNames={textInputBoxClass}
              label="App Icon"
              required={true}
              inputFactory={() => (
                <div className="min-h-[150px] flex flex-col items-center justify-center border-2 border-dashed border-[#ccc] rounded-[8px] p-[20px] text-center relative mt-2">
                  <input
                    type="file"
                    id="appIconFile"
                    accept=".png"
                    className="hidden"
                    onChange={(e) => {
                      const file = e.target.files[0];
                      if (file) {
                        const fileName = file.name.toLowerCase();
                        if (!fileName.endsWith(".png")) {
                          toast.warn("App icon only accepts .png format");
                          e.target.value = "";
                          return;
                        }
                        const reader = new FileReader();
                        reader.onload = (ev) => {
                          setUserInput((prev) => ({ ...prev, appIcon: ev.target.result }));
                        };
                        reader.readAsDataURL(file);
                      }
                    }}
                  />

                  {userInput?.appIcon ? (
                    <div className="relative inline-block">
                      <img
                        src={userInput?.appIcon}
                        alt="Preview"
                        className="max-w-[150px] max-h-[80px] block"
                      />
                      <button
                        type="button"
                        onClick={() => setUserInput((prev) => ({ ...prev, appIcon: "" }))}
                        className="absolute -top-[10px] -right-[10px] bg-[#ff4444] text-white border-none rounded-full w-[24px] h-[24px] cursor-pointer text-[14px] leading-[1] flex items-center justify-center">
                        ×
                      </button>
                    </div>
                  ) : (
                    <PrimaryButton
                      classNames={["min-w-[130px]"]}
                      action={() => document.getElementById("appIconFile").click()}>
                      Browse
                    </PrimaryButton>
                  )}
                </div>
              )}
            />
          </GridCell>

          {!!userInput?.franchiseid && (
            <GridCell columnSpan={2}>
              <FormField
                id="siteBanner"
                classNames={textInputBoxClass}
                label="Site Banner"
                required={true}
                inputFactory={() => (
                  <div className="min-h-[150px] flex flex-col items-center justify-center border-2 border-dashed border-[#ccc] rounded-[8px] p-[20px] text-center relative mt-2">
                    <input
                      type="file"
                      id="siteBannerFile"
                      accept=".png"
                      className="hidden"
                      onChange={(e) => {
                        const file = e.target.files[0];
                        if (file) {
                          const fileName = file.name.toLowerCase();
                          if (!fileName.endsWith(".png")) {
                            toast.warn("Site banner only accepts .png format");
                            e.target.value = "";
                            return;
                          }
                          const reader = new FileReader();
                          reader.onload = (ev) => {
                            setUserInput((prev) => ({
                              ...prev,
                              appConfig: { ...prev.appConfig, siteBanner: ev.target.result },
                            }));
                          };
                          reader.readAsDataURL(file);
                        }
                      }}
                    />

                    {userInput?.appConfig?.siteBanner ? (
                      <div className="relative inline-block">
                        <img
                          src={userInput?.appConfig?.siteBanner}
                          alt="Preview"
                          className="max-w-[150px] max-h-[80px] block"
                        />
                        <button
                          type="button"
                          onClick={() =>
                            setUserInput((prev) => ({
                              ...prev,
                              appConfig: { ...prev.appConfig, siteBanner: "" },
                            }))
                          }
                          className="absolute -top-[10px] -right-[10px] bg-[#ff4444] text-white border-none rounded-full w-[24px] h-[24px] cursor-pointer text-[14px] leading-[1] flex items-center justify-center">
                          ×
                        </button>
                      </div>
                    ) : (
                      <PrimaryButton
                        classNames={["min-w-[130px]"]}
                        action={() => document.getElementById("siteBannerFile").click()}>
                        Browse
                      </PrimaryButton>
                    )}
                  </div>
                )}
              />
            </GridCell>
          )}

          {/* MAIL CONFIG */}
          <GridCell columnSpan={4} classNames={["mt-6"]}>
            <PageTitle title="Mail Config" />
          </GridCell>

          <GridCell columnSpan={2}>
            <FormField
              id="service"
              classNames={textInputBoxClass}
              label="Service"
              required={true}
              inputFactory={(id, name, onValidation) => (
                <TextInput
                  id={id}
                  name={name}
                  isCopyable={true}
                  value={userInput?.mailSettings?.service}
                  placeholder="Enter Service"
                  onValidation={onValidation}
                  onChange={(_, n) =>
                    setUserInput((prev) => ({
                      ...prev,
                      mailSettings: { ...prev.mailSettings, service: n },
                    }))
                  }
                />
              )}
            />
          </GridCell>

          <GridCell columnSpan={2}>
            <FormField
              id="mailPort"
              classNames={textInputBoxClass}
              label="Mail Port"
              required={true}
              inputFactory={(id, name, onValidation) => (
                <TextInput
                  id={id}
                  name={name}
                  isCopyable={true}
                  type="number"
                  min={0}
                  value={String(userInput?.mailSettings?.mailPort)}
                  placeholder="Enter Mail Port"
                  onValidation={onValidation}
                  onChange={(_, n) =>
                    setUserInput((prev) => ({
                      ...prev,
                      mailSettings: { ...prev.mailSettings, mailPort: parseInt(n) || 0 },
                    }))
                  }
                />
              )}
            />
          </GridCell>

          <GridCell columnSpan={2}>
            <FormField
              id="userName"
              classNames={textInputBoxClass}
              label="User Name"
              required={true}
              inputFactory={(id, name, onValidation) => (
                <TextInput
                  id={id}
                  name={name}
                  isCopyable={true}
                  value={userInput?.mailSettings?.userName}
                  placeholder="Enter User Name"
                  onValidation={onValidation}
                  onChange={(_, n) =>
                    setUserInput((prev) => ({
                      ...prev,
                      mailSettings: { ...prev.mailSettings, userName: n },
                    }))
                  }
                />
              )}
            />
          </GridCell>

          <GridCell columnSpan={2}>
            <FormField
              id="password"
              classNames={textInputBoxClass}
              label="Password"
              required={true}
              inputFactory={(id, name, onValidation) => (
                <MaskedTextInput
                  id={id}
                  name={name}
                  value={userInput?.mailSettings?.password}
                  placeholder="Enter Password"
                  onValidation={onValidation}
                  onChange={(_, n) =>
                    setUserInput((prev) => ({
                      ...prev,
                      mailSettings: { ...prev.mailSettings, password: n.replace(/\\/g, "") },
                    }))
                  }
                />
              )}
            />
          </GridCell>

          <GridCell columnSpan={2}>
            <FormField
              id="resetPasswordLink"
              classNames={textInputBoxClass}
              label="Reset Password Link"
              required={true}
              inputFactory={(id, name, onValidation) => (
                <TextInput
                  id={id}
                  name={name}
                  isCopyable={true}
                  type="url"
                  value={userInput?.mailSettings?.resetPasswordLink}
                  placeholder="Enter Reset Password Link"
                  onValidation={onValidation}
                  onChange={(_, n) =>
                    setUserInput((prev) => ({
                      ...prev,
                      mailSettings: { ...prev.mailSettings, resetPasswordLink: n },
                    }))
                  }
                />
              )}
            />
          </GridCell>

          {/* App Config */}
          <GridCell columnSpan={4} classNames={["mt-6"]}>
            <PageTitle title="App Config" />
          </GridCell>

          <GridCell columnSpan={2}>
            <FormField
              id="Address"
              classNames={textInputBoxClass}
              label="Address"
              inputFactory={(id, name, onValidation) => (
                <TextInput
                  id={id}
                  name={name}
                  isCopyable={true}
                  value={userInput?.appConfig?.Address}
                  placeholder="Enter Address"
                  onValidation={onValidation}
                  onChange={(_, n) =>
                    setUserInput((prev) => ({
                      ...prev,
                      appConfig: { ...prev.appConfig, Address: n },
                    }))
                  }
                />
              )}
            />
          </GridCell>

          <GridCell columnSpan={2}>
            <FormField
              id="Email"
              classNames={textInputBoxClass}
              label="Email"
              required={true}
              inputFactory={(id, name, onValidation) => (
                <TextInput
                  id={id}
                  name={name}
                  isCopyable={true}
                  type="email"
                  value={userInput?.appConfig?.Email}
                  placeholder="Enter Email"
                  onValidation={onValidation}
                  onChange={(_, n) =>
                    setUserInput((prev) => ({
                      ...prev,
                      appConfig: { ...prev.appConfig, Email: n },
                    }))
                  }
                />
              )}
            />
          </GridCell>

          <GridCell columnSpan={4}>
            <FormField
              id="Hours"
              classNames={textInputBoxClass}
              label="Hours"
              inputFactory={(id, name, onValidation) => (
                <TextAreaInput
                  id={id}
                  name={name}
                  isCopyable={true}
                  value={userInput?.appConfig?.Hours}
                  placeholder="Enter Hours"
                  onValidation={onValidation}
                  onChange={(_, n) =>
                    setUserInput((prev) => ({
                      ...prev,
                      appConfig: { ...prev.appConfig, Hours: n },
                    }))
                  }
                />
              )}
            />
          </GridCell>

          <GridCell columnSpan={2}>
            <FormField
              id="Website"
              classNames={textInputBoxClass}
              label="Website"
              inputFactory={(id, name, onValidation) => (
                <TextInput
                  id={id}
                  name={name}
                  isCopyable={true}
                  type="url"
                  value={userInput?.appConfig?.Website}
                  placeholder="Enter Website"
                  onValidation={onValidation}
                  onChange={(_, n) =>
                    setUserInput((prev) => ({
                      ...prev,
                      appConfig: { ...prev.appConfig, Website: n },
                    }))
                  }
                />
              )}
            />
          </GridCell>

          <GridCell columnSpan={2}>
            <FormField
              id="myUsualWebsite"
              classNames={textInputBoxClass}
              label="My Usual Website"
              inputFactory={(id, name, onValidation) => (
                <TextInput
                  id={id}
                  name={name}
                  isCopyable={true}
                  type="url"
                  value={userInput?.appConfig?.myUsualWebsite}
                  placeholder="Enter My Usual Website"
                  onValidation={onValidation}
                  onChange={(_, n) =>
                    setUserInput((prev) => ({
                      ...prev,
                      appConfig: { ...prev.appConfig, myUsualWebsite: n },
                    }))
                  }
                />
              )}
            />
          </GridCell>

          <GridCell columnSpan={2}>
            <FormField
              id="inStoreLoyaltyId"
              classNames={textInputBoxClass}
              label="In Store Loyalty Id"
              inputFactory={(id, name, onValidation) => (
                <TextInput
                  id={id}
                  name={name}
                  isCopyable={true}
                  value={userInput?.appConfig?.inStoreLoyaltyId}
                  placeholder="Enter In Store Loyalty Id"
                  onValidation={onValidation}
                  onChange={(_, n) =>
                    setUserInput((prev) => ({
                      ...prev,
                      appConfig: { ...prev.appConfig, inStoreLoyaltyId: n },
                    }))
                  }
                />
              )}
            />
          </GridCell>

          <GridCell columnSpan={2}>
            <FormField
              id="InstagramLink"
              classNames={textInputBoxClass}
              label="Instagram Link"
              inputFactory={(id, name, onValidation) => (
                <TextInput
                  id={id}
                  name={name}
                  isCopyable={true}
                  type="url"
                  value={userInput?.appConfig?.InstagramLink}
                  placeholder="Enter Instagram Link"
                  onValidation={onValidation}
                  onChange={(_, n) =>
                    setUserInput((prev) => ({
                      ...prev,
                      appConfig: { ...prev.appConfig, InstagramLink: n },
                    }))
                  }
                />
              )}
            />
          </GridCell>

          <GridCell columnSpan={2}>
            <FormField
              id="FacebookLink"
              classNames={textInputBoxClass}
              label="Facebook Link"
              inputFactory={(id, name, onValidation) => (
                <TextInput
                  id={id}
                  name={name}
                  isCopyable={true}
                  type="url"
                  value={userInput?.appConfig?.FacebookLink}
                  placeholder="Enter Facebook Link"
                  onValidation={onValidation}
                  onChange={(_, n) =>
                    setUserInput((prev) => ({
                      ...prev,
                      appConfig: { ...prev.appConfig, FacebookLink: n },
                    }))
                  }
                />
              )}
            />
          </GridCell>

          <GridCell columnSpan={2}>
            <FormField
              id="AcknowledgementsLink"
              classNames={textInputBoxClass}
              label="Acknowledgements Link"
              inputFactory={(id, name, onValidation) => (
                <TextInput
                  id={id}
                  name={name}
                  isCopyable={true}
                  type="url"
                  value={userInput?.appConfig?.AcknowledgementsLink}
                  placeholder="Enter Acknowledgements Link"
                  onValidation={onValidation}
                  onChange={(_, n) =>
                    setUserInput((prev) => ({
                      ...prev,
                      appConfig: { ...prev.appConfig, AcknowledgementsLink: n },
                    }))
                  }
                />
              )}
            />
          </GridCell>

          <GridCell columnSpan={2}>
            <FormField
              id="PrivacyLink"
              classNames={textInputBoxClass}
              label="Privacy Link"
              inputFactory={(id, name, onValidation) => (
                <TextInput
                  id={id}
                  name={name}
                  isCopyable={true}
                  type="url"
                  value={userInput?.appConfig?.PrivacyLink}
                  placeholder="Enter Privacy Link"
                  onValidation={onValidation}
                  onChange={(_, n) =>
                    setUserInput((prev) => ({
                      ...prev,
                      appConfig: { ...prev.appConfig, PrivacyLink: n },
                    }))
                  }
                />
              )}
            />
          </GridCell>

          <GridCell columnSpan={2}>
            <FormField
              id="TermsLink"
              classNames={textInputBoxClass}
              label="Terms Link"
              inputFactory={(id, name, onValidation) => (
                <TextInput
                  id={id}
                  name={name}
                  isCopyable={true}
                  type="url"
                  value={userInput?.appConfig?.TermsLink}
                  placeholder="Enter Terms Link"
                  onValidation={onValidation}
                  onChange={(_, n) =>
                    setUserInput((prev) => ({
                      ...prev,
                      appConfig: { ...prev.appConfig, TermsLink: n },
                    }))
                  }
                />
              )}
            />
          </GridCell>

          <GridCell columnSpan={2}>
            <FormField
              id="TermsLinkStaff"
              classNames={textInputBoxClass}
              label="Staff Terms Link"
              inputFactory={(id, name, onValidation) => (
                <TextInput
                  id={id}
                  name={name}
                  isCopyable={true}
                  type="url"
                  value={userInput?.appConfig?.TermsLinkStaff}
                  placeholder="Enter Staff Terms Link"
                  onValidation={onValidation}
                  onChange={(_, n) =>
                    setUserInput((prev) => ({
                      ...prev,
                      appConfig: { ...prev.appConfig, TermsLinkStaff: n },
                    }))
                  }
                />
              )}
            />
          </GridCell>

          <GridCell columnSpan={2}>
            <FormField
              id="RefundLink"
              classNames={textInputBoxClass}
              label="Refund Link"
              inputFactory={(id, name, onValidation) => (
                <TextInput
                  id={id}
                  name={name}
                  isCopyable={true}
                  type="url"
                  value={userInput?.appConfig?.RefundLink}
                  placeholder="Enter Refund Link"
                  onValidation={onValidation}
                  onChange={(_, n) =>
                    setUserInput((prev) => ({
                      ...prev,
                      appConfig: { ...prev.appConfig, RefundLink: n },
                    }))
                  }
                />
              )}
            />
          </GridCell>

          {/* Force Update */}
          <GridCell columnSpan={4} classNames={["mt-6"]}>
            <PageTitle title="Force Update" />
          </GridCell>

          <GridCell columnSpan={2}>
            <FormField
              id="androidMinimumVersion"
              classNames={textInputBoxClass}
              label="Minimum Version (Android)"
              inputFactory={(id, name, onValidation) => (
                <TextInput
                  id={id}
                  name={name}
                  isCopyable={true}
                  value={userInput?.appConfig?.androidVersions?.minimumVersion}
                  placeholder="Enter Minimum Version (Android)"
                  onValidation={onValidation}
                  onChange={(_, n) =>
                    setUserInput((prev) => ({
                      ...prev,
                      appConfig: {
                        ...prev.appConfig,
                        androidVersions: { ...prev.appConfig.androidVersions, minimumVersion: n },
                      },
                    }))
                  }
                />
              )}
            />
          </GridCell>

          <GridCell columnSpan={2}>
            <FormField
              id="androidUrl"
              classNames={textInputBoxClass}
              label="URL (Play Store)"
              inputFactory={(id, name, onValidation) => (
                <TextInput
                  id={id}
                  name={name}
                  isCopyable={true}
                  value={userInput?.appConfig?.androidVersions?.url}
                  placeholder="Enter URL (Play Store)"
                  onValidation={onValidation}
                  onChange={(_, n) =>
                    setUserInput((prev) => ({
                      ...prev,
                      appConfig: {
                        ...prev.appConfig,
                        androidVersions: { ...prev.appConfig.androidVersions, url: n },
                      },
                    }))
                  }
                />
              )}
            />
          </GridCell>

          <GridCell columnSpan={2}>
            <FormField
              id="iosMinimumVersion"
              classNames={textInputBoxClass}
              label="Minimum Version (IOS)"
              inputFactory={(id, name, onValidation) => (
                <TextInput
                  id={id}
                  name={name}
                  isCopyable={true}
                  value={userInput?.appConfig?.iosVersions?.minimumVersion}
                  placeholder="Enter Minimum Version (IOS)"
                  onValidation={onValidation}
                  onChange={(_, n) =>
                    setUserInput((prev) => ({
                      ...prev,
                      appConfig: {
                        ...prev.appConfig,
                        iosVersions: { ...prev.appConfig.iosVersions, minimumVersion: n },
                      },
                    }))
                  }
                />
              )}
            />
          </GridCell>

          <GridCell columnSpan={2}>
            <FormField
              id="iosUrl"
              classNames={textInputBoxClass}
              label="URL (App Store)"
              inputFactory={(id, name, onValidation) => (
                <TextInput
                  id={id}
                  name={name}
                  isCopyable={true}
                  value={userInput?.appConfig?.iosVersions?.url}
                  placeholder="Enter URL (App Store)"
                  onValidation={onValidation}
                  onChange={(_, n) =>
                    setUserInput((prev) => ({
                      ...prev,
                      appConfig: {
                        ...prev.appConfig,
                        iosVersions: { ...prev.appConfig.iosVersions, url: n },
                      },
                    }))
                  }
                />
              )}
            />
          </GridCell>

          {/* Location */}
          <GridCell columnSpan={4} classNames={["mt-6"]}>
            <PageTitle title="Location" />
          </GridCell>

          <GridCell columnSpan={2}>
            <FormField
              id="latitude"
              classNames={textInputBoxClass}
              label="Latitude"
              required={true}
              inputFactory={(id, name, onValidation) => (
                <FloatInput
                  id={id}
                  name={name}
                  isCopyable={true}
                  type="number"
                  min={0}
                  value={userInput?.appConfig?.Location?.latitude}
                  placeholder="Enter Latitude"
                  onChange={(_, n) =>
                    setUserInput((prev) => ({
                      ...prev,
                      appConfig: {
                        ...prev.appConfig,
                        Location: { ...prev.appConfig.Location, latitude: n },
                      },
                    }))
                  }
                />
              )}
            />
          </GridCell>

          <GridCell columnSpan={2}>
            <FormField
              id="latitudeDelta"
              classNames={textInputBoxClass}
              label="Latitude Delta"
              required={true}
              inputFactory={(id, name, onValidation) => (
                <FloatInput
                  id={id}
                  name={name}
                  placeholder="Enter Latitude Delta"
                  value={userInput?.appConfig?.Location?.latitudeDelta}
                  onChange={(_, n) =>
                    setUserInput((prev) => ({
                      ...prev,
                      appConfig: {
                        ...prev.appConfig,
                        Location: { ...prev.appConfig.Location, latitudeDelta: n },
                      },
                    }))
                  }
                />
              )}
            />
          </GridCell>

          <GridCell columnSpan={2}>
            <FormField
              id="longitude"
              classNames={textInputBoxClass}
              label="Longitude"
              required={true}
              inputFactory={(id, name, onValidation) => (
                <FloatInput
                  id={id}
                  name={name}
                  placeholder="Enter Longitude"
                  value={userInput?.appConfig?.Location?.longitude}
                  onChange={(_, n) =>
                    setUserInput((prev) => ({
                      ...prev,
                      appConfig: {
                        ...prev.appConfig,
                        Location: { ...prev.appConfig.Location, longitude: n },
                      },
                    }))
                  }
                />
              )}
            />
          </GridCell>

          <GridCell columnSpan={2}>
            <FormField
              id="longitudeDelta"
              classNames={textInputBoxClass}
              label="Longitude Delta"
              required={true}
              inputFactory={(id, name, onValidation) => (
                <FloatInput
                  id={id}
                  name={name}
                  placeholder="Enter Longitude Delta"
                  value={userInput?.appConfig?.Location?.longitudeDelta}
                  onChange={(_, n) =>
                    setUserInput((prev) => ({
                      ...prev,
                      appConfig: {
                        ...prev.appConfig,
                        Location: {
                          ...prev.appConfig.Location,
                          longitudeDelta: n,
                        },
                      },
                    }))
                  }
                />
              )}
            />
          </GridCell>

          {/* SMS / TWILLIO */}
          <GridCell columnSpan={4} classNames={["mt-6"]}>
            <PageTitle title="SMS (Twillio)" />
          </GridCell>

          <GridCell columnSpan={2}>
            <FormField
              id="accountSid"
              classNames={textInputBoxClass}
              label="Account SID"
              required={true}
              inputFactory={(id, name, onValidation) => (
                <MaskedTextInput
                  id={id}
                  name={name}
                  value={userInput?.twilio?.accountSid}
                  placeholder="Enter Account SID"
                  onValidation={onValidation}
                  onChange={(_, n) =>
                    setUserInput((prev) => ({ ...prev, twilio: { ...prev.twilio, accountSid: n } }))
                  }
                />
              )}
            />
          </GridCell>

          <GridCell columnSpan={2}>
            <FormField
              id="apiKeySecret"
              classNames={textInputBoxClass}
              label="Auth Secret"
              required={true}
              inputFactory={(id, name, onValidation) => (
                <MaskedTextInput
                  id={id}
                  name={name}
                  value={userInput?.twilio?.apiKeySecret}
                  placeholder="Enter Auth Secret"
                  onValidation={onValidation}
                  onChange={(_, n) =>
                    setUserInput((prev) => ({ ...prev, twilio: { ...prev.twilio, apiKeySecret: n } }))
                  }
                />
              )}
            />
          </GridCell>

          <GridCell columnSpan={2}>
            <FormField
              id="from"
              classNames={textInputBoxClass}
              label="From"
              required={true}
              inputFactory={(id, name, onValidation) => (
                <MaskedTextInput
                  id={id}
                  name={name}
                  value={userInput?.twilio?.from}
                  placeholder="Enter From"
                  onValidation={onValidation}
                  onChange={(_, n) =>
                    setUserInput((prev) => ({ ...prev, twilio: { ...prev.twilio, from: n } }))
                  }
                />
              )}
            />
          </GridCell>

          <GridCell columnSpan={2}>
            <FormField
              id="apiKeySid"
              classNames={textInputBoxClass}
              label="Api Key Sid"
              required={true}
              inputFactory={(id, name, onValidation) => (
                <MaskedTextInput
                  id={id}
                  name={name}
                  value={userInput?.twilio?.apiKeySid}
                  placeholder="Enter Api Key Sid"
                  onValidation={onValidation}
                  onChange={(_, n) =>
                    setUserInput((prev) => ({
                      ...prev,
                      twilio: { ...prev.twilio, apiKeySid: n },
                    }))
                  }
                />
              )}
            />
          </GridCell>

          {/* Notifications */}
          <GridCell columnSpan={4} classNames={["mt-6"]}>
            <PageTitle title="Notifications (One Singal)" />
          </GridCell>

          <GridCell columnSpan={2}>
            <FormField
              id="apiKey"
              classNames={textInputBoxClass}
              label="API Key"
              required={true}
              inputFactory={(id, name, onValidation) => (
                <MaskedTextInput
                  id={id}
                  name={name}
                  value={userInput?.oneSignal?.apiKey}
                  placeholder="Enter API Key"
                  onValidation={onValidation}
                  onChange={(_, n) =>
                    setUserInput((prev) => ({
                      ...prev,
                      oneSignal: { ...prev.oneSignal, apiKey: n },
                    }))
                  }
                />
              )}
            />
          </GridCell>

          <GridCell columnSpan={2}>
            <FormField
              id="onesignalAppId"
              classNames={textInputBoxClass}
              label="App Id"
              required={true}
              inputFactory={(id, name, onValidation) => (
                <MaskedTextInput
                  id={id}
                  name={name}
                  value={userInput?.oneSignal?.appId}
                  placeholder="Enter App Id"
                  onValidation={onValidation}
                  onChange={(_, n) =>
                    setUserInput((prev) => ({
                      ...prev,
                      oneSignal: { ...prev.oneSignal, appId: n },
                    }))
                  }
                />
              )}
            />
          </GridCell>

          <GridCell columnSpan={2}>
            <FormField
              id="staffApiKey"
              classNames={textInputBoxClass}
              label="Staff API Key"
              required={true}
              inputFactory={(id, name, onValidation) => (
                <MaskedTextInput
                  id={id}
                  name={name}
                  value={userInput?.oneSignal?.staffApiKey}
                  placeholder="Enter Staff API Key"
                  onValidation={onValidation}
                  onChange={(_, n) =>
                    setUserInput((prev) => ({
                      ...prev,
                      oneSignal: { ...prev.oneSignal, staffApiKey: n },
                    }))
                  }
                />
              )}
            />
          </GridCell>

          <GridCell columnSpan={2}>
            <FormField
              id="staffAppId"
              classNames={textInputBoxClass}
              label="Staff App Id"
              required={true}
              inputFactory={(id, name, onValidation) => (
                <MaskedTextInput
                  id={id}
                  name={name}
                  value={userInput?.oneSignal?.staffAppId}
                  placeholder="Enter Staff App Id"
                  onValidation={onValidation}
                  onChange={(_, n) =>
                    setUserInput((prev) => ({
                      ...prev,
                      oneSignal: { ...prev.oneSignal, staffAppId: n },
                    }))
                  }
                />
              )}
            />
          </GridCell>

          <GridCell columnSpan={2}>
            <FormField
              id="notificationUrl"
              classNames={textInputBoxClass}
              label="Notification URL"
              inputFactory={(id, name, onValidation) => (
                <TextInput
                  id={id}
                  name={name}
                  isCopyable={true}
                  type="url"
                  value={userInput?.webhook?.notificationUrl}
                  placeholder="Enter notificationUrl"
                  onValidation={onValidation}
                  onChange={(_, n) =>
                    setUserInput((prev) => ({
                      ...prev,
                      webhook: { ...prev.webhook, notificationUrl: n },
                    }))
                  }
                />
              )}
            />
          </GridCell>

          <GridCell columnSpan={2}>
            <FormField
              id="signatureKey"
              classNames={textInputBoxClass}
              label="Signature Key"
              required={true}
              inputFactory={(id, name, onValidation) => (
                <MaskedTextInput
                  id={id}
                  name={name}
                  value={userInput?.webhook?.signatureKey}
                  placeholder="Enter signatureKey"
                  onValidation={onValidation}
                  onChange={(_, n) =>
                    setUserInput((prev) => ({
                      ...prev,
                      webhook: { ...prev.webhook, signatureKey: n },
                    }))
                  }
                />
              )}
            />
          </GridCell>

          {/* Colors */}
          <GridCell columnSpan={4} classNames={["mt-6"]}>
            <PageTitle title="Colors" />
          </GridCell>

          <GridCell columnSpan={2}>
            <FormField
              id="primaryColor"
              classNames={textInputBoxClass}
              label="Primary Color"
              inputFactory={(id, name, onValidation) => (
                <TextInput
                  id={id}
                  name={name}
                  isCopyable={true}
                  value={userInput?.appConfig?.colors?.primaryColor}
                  placeholder="Enter Primary Color (e.g. #000)"
                  onValidation={onValidationWrapper(id, onValidation)}
                  onChange={(_, n) =>
                    setUserInput((prev) => ({
                      ...prev,
                      appConfig: {
                        ...prev.appConfig,
                        colors: { ...prev.appConfig.colors, primaryColor: n },
                      },
                    }))
                  }
                  validationRules={colorCodeValidationRules}
                />
              )}
            />
          </GridCell>

          <GridCell columnSpan={2}>
            <FormField
              id="secondaryColor"
              classNames={textInputBoxClass}
              label="Secondary Color"
              inputFactory={(id, name, onValidation) => (
                <TextInput
                  id={id}
                  name={name}
                  isCopyable={true}
                  value={userInput?.appConfig?.colors?.secondaryColor}
                  placeholder="Enter Secondary Color (e.g. #000)"
                  onValidation={onValidationWrapper(id, onValidation)}
                  onChange={(_, n) =>
                    setUserInput((prev) => ({
                      ...prev,
                      appConfig: {
                        ...prev.appConfig,
                        colors: { ...prev.appConfig.colors, secondaryColor: n },
                      },
                    }))
                  }
                  validationRules={colorCodeValidationRules}
                />
              )}
            />
          </GridCell>

                    <GridCell columnSpan={4} classNames={["mt-6"]}>
                      <div className="relative">
                        <PageTitle title="Languages" />
                        <div className="absolute right-0 top-0 h-full flex items-center pr-2">
                          <PrimaryButton
                            classNames={["min-w-[150px]"]}
                            action={() =>
                              setUserInput((prev) => ({
                                ...prev,
                                languages: [...(prev.languages || []), { code: "", key: "", name: "" }],
                              }))
                            }>
                            + Add Language
                          </PrimaryButton>
                        </div>
                      </div>
                    </GridCell>

                    {(userInput?.languages || []).map((lang, index) => (
                      <GridCell key={`lang-${index}`} columnSpan={4}>
                        {/* Mobile: card with delete button at top-right */}
                        <div className="md:hidden relative border border-gray-200 rounded-lg p-3 pt-8">
                          <button
                            type="button"
                            onClick={() =>
                              setUserInput((prev) => ({
                                ...prev,
                                languages: (prev.languages || []).filter((_, i) => i !== index),
                              }))
                            }
                            className="absolute top-2 right-2 bg-[#ff4444] text-white border-none rounded-full w-[24px] h-[24px] cursor-pointer text-[14px] flex items-center justify-center">
                            ×
                          </button>
                          <div className="grid grid-cols-2 gap-3">
                            <FormField
                              id={`languages.${index}.code`}
                              classNames={textInputBoxClass}
                              label="Code"
                              required={true}
                              inputFactory={(id, name) => (
                                <div className="mb-1">
                                  <SelectInput
                                    id={id}
                                    name={name}
                                    placeholder="Select Code"
                                    selectedKey={lang.code}
                                    optionProvider={() => languageCodeOptions}
                                    keySelector={(x) => x.value}
                                    labelSelector={(x) => x.label}
                                    isClearable={true}
                                    onChange={(_, n) =>
                                      setUserInput((prev) => {
                                        const langs = [...(prev.languages || [])];
                                        langs[index] = { ...langs[index], code: n ? n.value : "" };
                                        return { ...prev, languages: langs };
                                      })
                                    }
                                  />
                                </div>
                              )}
                            />
                            <FormField
                              id={`languages.${index}.key`}
                              classNames={textInputBoxClass}
                              label="Key"
                              required={true}
                              inputFactory={(id, name) => (
                                <div className="mb-1">
                                  <SelectInput
                                    id={id}
                                    name={name}
                                    placeholder="Select Key"
                                    selectedKey={lang.key}
                                    optionProvider={() => languageKeyOptions}
                                    keySelector={(x) => x.value}
                                    labelSelector={(x) => x.label}
                                    isClearable={true}
                                    onChange={(_, n) =>
                                      setUserInput((prev) => {
                                        const langs = [...(prev.languages || [])];
                                        langs[index] = { ...langs[index], key: n ? n.value : "" };
                                        return { ...prev, languages: langs };
                                      })
                                    }
                                  />
                                </div>
                              )}
                            />
                            <div className="col-span-2">
                              <FormField
                                id={`languages.${index}.name`}
                                classNames={textInputBoxClass}
                                label="Name"
                                required={true}
                                inputFactory={(id, name) => (
                                  <div className="mb-1">
                                    <SelectInput
                                      id={id}
                                      name={name}
                                      placeholder="Select Name"
                                      selectedKey={lang.name}
                                      optionProvider={() => languageNameOptions}
                                      keySelector={(x) => x.value}
                                      labelSelector={(x) => x.label}
                                      isClearable={true}
                                      onChange={(_, n) =>
                                        setUserInput((prev) => {
                                          const langs = [...(prev.languages || [])];
                                          langs[index] = { ...langs[index], name: n ? n.value : "" };
                                          return { ...prev, languages: langs };
                                        })
                                      }
                                    />
                                  </div>
                                )}
                              />
                            </div>
                          </div>
                        </div>

                        {/* Desktop: single row layout */}
                        <div className="hidden md:grid grid-cols-[1fr_1fr_1fr_auto] gap-3 items-start">
                          <FormField
                            id={`languages.${index}.code`}
                            classNames={textInputBoxClass}
                            label="Code"
                            required={true}
                            inputFactory={(id, name) => (
                              <div className="mb-1">
                                <SelectInput
                                  id={id}
                                  name={name}
                                  placeholder="Select Code"
                                  selectedKey={lang.code}
                                  optionProvider={() => languageCodeOptions}
                                  keySelector={(x) => x.value}
                                  labelSelector={(x) => x.label}
                                  isClearable={true}
                                  onChange={(_, n) =>
                                    setUserInput((prev) => {
                                      const langs = [...(prev.languages || [])];
                                      langs[index] = { ...langs[index], code: n ? n.value : "" };
                                      return { ...prev, languages: langs };
                                    })
                                  }
                                />
                              </div>
                            )}
                          />
                          <FormField
                            id={`languages.${index}.key`}
                            classNames={textInputBoxClass}
                            label="Key"
                            required={true}
                            inputFactory={(id, name) => (
                              <div className="mb-1">
                                <SelectInput
                                  id={id}
                                  name={name}
                                  placeholder="Select Key"
                                  selectedKey={lang.key}
                                  optionProvider={() => languageKeyOptions}
                                  keySelector={(x) => x.value}
                                  labelSelector={(x) => x.label}
                                  isClearable={true}
                                  onChange={(_, n) =>
                                    setUserInput((prev) => {
                                      const langs = [...(prev.languages || [])];
                                      langs[index] = { ...langs[index], key: n ? n.value : "" };
                                      return { ...prev, languages: langs };
                                    })
                                  }
                                />
                              </div>
                            )}
                          />
                          <FormField
                            id={`languages.${index}.name`}
                            classNames={textInputBoxClass}
                            label="Name"
                            required={true}
                            inputFactory={(id, name) => (
                              <div className="mb-1">
                                <SelectInput
                                  id={id}
                                  name={name}
                                  placeholder="Select Name"
                                  selectedKey={lang.name}
                                  optionProvider={() => languageNameOptions}
                                  keySelector={(x) => x.value}
                                  labelSelector={(x) => x.label}
                                  isClearable={true}
                                  onChange={(_, n) =>
                                    setUserInput((prev) => {
                                      const langs = [...(prev.languages || [])];
                                      langs[index] = { ...langs[index], name: n ? n.value : "" };
                                      return { ...prev, languages: langs };
                                    })
                                  }
                                />
                              </div>
                            )}
                          />
                          <div className="place-self-center pt-5">
                            <button
                              type="button"
                              onClick={() =>
                                setUserInput((prev) => ({
                                  ...prev,
                                  languages: (prev.languages || []).filter((_, i) => i !== index),
                                }))
                              }
                              className="bg-[#ff4444] text-white border-none rounded-full w-[24px] h-[24px] cursor-pointer text-[14px] flex items-center justify-center">
                              ×
                            </button>
                          </div>
                        </div>
                      </GridCell>
                    ))}

          {/* POS */}
          <GridCell columnSpan={4} classNames={["mt-6"]}>
            <PageTitle title="POS" />
          </GridCell>

          <GridCell columnSpan={2}>
            <FormField
              id="posIntegration"
              classNames={textInputBoxClass}
              inputFactory={() => (
                <>
                  <InRow gap={1.5} classNames={["flex-col !items-start mt-3"]}>
                    <InRow gap={2} classNames={["w-full !justify-between"]}>
                      <label className="m-0">Square POS</label>
                      <ToggleButton
                        checked={userInput?.squareSync}
                        onChange={(checked) => handlePosToggle("squareSync", checked)}
                      />
                    </InRow>
                    <InRow gap={2} classNames={["w-full !justify-between"]}>
                      <label className="m-0">Triniteq POS</label>
                      <ToggleButton
                        checked={userInput?.triniteqPos}
                        onChange={(checked) => handlePosToggle("triniteqPos", checked)}
                      />
                    </InRow>
                    <InRow gap={2} classNames={["w-full !justify-between"]}>
                      <label className="m-0">Tevalis POS</label>
                      <ToggleButton
                        checked={userInput?.tevalisPos}
                        onChange={(checked) => handlePosToggle("tevalisPos", checked)}
                      />
                    </InRow>
                  </InRow>
                </>
              )}
            />
          </GridCell>

          <GridCell columnSpan={2}></GridCell>

          {userInput?.triniteqPos && (
            <>
              {/* Triniteq POS */}
              <GridCell columnSpan={4} classNames={["mt-6"]}>
                <PageTitle title="Triniteq POS" />
              </GridCell>

              <GridCell columnSpan={2}>
                <FormField
                  id="grant_type"
                  classNames={textInputBoxClass}
                  label="Grant Type"
                  inputFactory={(id, name, onValidation) => (
                    <TextInput
                      id={id}
                      name={name}
                      isCopyable={true}
                      value={userInput?.triniteq?.grant_type}
                      placeholder="Enter grant_type"
                      onValidation={onValidation}
                      onChange={(_, n) =>
                        setUserInput((prev) => ({
                          ...prev,
                          triniteq: { ...prev.triniteq, grant_type: n },
                        }))
                      }
                    />
                  )}
                />
              </GridCell>

              <GridCell columnSpan={2}>
                <FormField
                  id="signature"
                  classNames={textInputBoxClass}
                  label="Signature"
                  required={true}
                  inputFactory={(id, name, onValidation) => (
                    <MaskedTextInput
                      id={id}
                      name={name}
                      value={userInput?.triniteq?.signature}
                      placeholder="Enter signature"
                      onValidation={onValidation}
                      onChange={(_, n) =>
                        setUserInput((prev) => ({
                          ...prev,
                          triniteq: { ...prev.triniteq, signature: n },
                        }))
                      }
                    />
                  )}
                />
              </GridCell>

              <GridCell columnSpan={2}>
                <FormField
                  id="providerID"
                  classNames={textInputBoxClass}
                  label="Provider Id"
                  required={true}
                  inputFactory={(id, name, onValidation) => (
                    <MaskedTextInput
                      id={id}
                      name={name}
                      value={userInput?.triniteq?.providerID}
                      placeholder="Enter Provider Id"
                      onValidation={onValidation}
                      onChange={(_, n) =>
                        setUserInput((prev) => ({
                          ...prev,
                          triniteq: { ...prev.triniteq, providerID: n },
                        }))
                      }
                    />
                  )}
                />
              </GridCell>

              <GridCell columnSpan={2}>
                <FormField
                  id="org_id"
                  classNames={textInputBoxClass}
                  label="Org Id"
                  required={true}
                  inputFactory={(id, name, onValidation) => (
                    <MaskedTextInput
                      id={id}
                      name={name}
                      value={userInput?.triniteq?.org_id}
                      placeholder="Enter Org Id"
                      onValidation={onValidation}
                      onChange={(_, n) =>
                        setUserInput((prev) => ({
                          ...prev,
                          triniteq: { ...prev.triniteq, org_id: n },
                        }))
                      }
                    />
                  )}
                />
              </GridCell>

              <GridCell columnSpan={2}>
                <FormField
                  id="outletID"
                  classNames={textInputBoxClass}
                  label="Outlet Id"
                  required={true}
                  inputFactory={(id, name, onValidation) => (
                    <MaskedTextInput
                      id={id}
                      name={name}
                      value={userInput?.triniteq?.outletID}
                      placeholder="Enter Outlet Id"
                      onValidation={onValidation}
                      onChange={(_, n) =>
                        setUserInput((prev) => ({
                          ...prev,
                          triniteq: { ...prev.triniteq, outletID: n },
                        }))
                      }
                    />
                  )}
                />
              </GridCell>

              <GridCell columnSpan={2}>
                <FormField
                  id="scope"
                  classNames={textInputBoxClass}
                  label="Scope"
                  inputFactory={(id, name, onValidation) => (
                    <TextInput
                      id={id}
                      name={name}
                      isCopyable={true}
                      value={userInput?.triniteq?.scope}
                      placeholder="Enter scope"
                      onValidation={onValidation}
                      onChange={(_, n) =>
                        setUserInput((prev) => ({
                          ...prev,
                          triniteq: { ...prev.triniteq, scope: n },
                        }))
                      }
                    />
                  )}
                />
              </GridCell>

              <GridCell columnSpan={2}>
                <FormField
                  id="client_secret"
                  classNames={textInputBoxClass}
                  label="Client Secret"
                  required={true}
                  inputFactory={(id, name, onValidation) => (
                    <MaskedTextInput
                      id={id}
                      name={name}
                      value={userInput?.triniteq?.client_secret}
                      placeholder="Enter Client Secret"
                      onValidation={onValidation}
                      onChange={(_, n) =>
                        setUserInput((prev) => ({
                          ...prev,
                          triniteq: { ...prev.triniteq, client_secret: n },
                        }))
                      }
                    />
                  )}
                />
              </GridCell>

              <GridCell columnSpan={2}>
                <FormField
                  id="client_id"
                  classNames={textInputBoxClass}
                  label="Client Id"
                  required={true}
                  inputFactory={(id, name, onValidation) => (
                    <MaskedTextInput
                      id={id}
                      name={name}
                      value={userInput?.triniteq?.client_id}
                      placeholder="Enter Client Id"
                      onValidation={onValidation}
                      onChange={(_, n) =>
                        setUserInput((prev) => ({
                          ...prev,
                          triniteq: { ...prev.triniteq, client_id: n },
                        }))
                      }
                    />
                  )}
                />
              </GridCell>

              <GridCell columnSpan={2}>
                <FormField
                  id="providerName"
                  classNames={textInputBoxClass}
                  label="Provider Name"
                  inputFactory={(id, name, onValidation) => (
                    <TextInput
                      id={id}
                      name={name}
                      isCopyable={true}
                      value={userInput?.triniteq?.providerName}
                      placeholder="Enter Provider Name"
                      onValidation={onValidation}
                      onChange={(_, n) =>
                        setUserInput((prev) => ({
                          ...prev,
                          triniteq: { ...prev.triniteq, providerName: n },
                        }))
                      }
                    />
                  )}
                />
              </GridCell>

              <GridCell columnSpan={2}></GridCell>
            </>
          )}

          {userInput?.tevalisPos && (
            <>
              {/* Tevalis POS */}
              <GridCell columnSpan={4} classNames={["mt-6"]}>
                <PageTitle title="Tevalis POS" />
              </GridCell>

              <GridCell columnSpan={2}>
                <FormField
                  id="CompanyId"
                  classNames={textInputBoxClass}
                  label="Company Id"
                  required={true}
                  inputFactory={(id, name, onValidation) => (
                    <MaskedTextInput
                      id={id}
                      name={name}
                      value={userInput?.tevalis?.CompanyId}
                      placeholder="Enter Company Id"
                      onValidation={onValidation}
                      onChange={(_, n) =>
                        setUserInput((prev) => ({
                          ...prev,
                          tevalis: { ...prev.tevalis, CompanyId: n },
                        }))
                      }
                    />
                  )}
                />
              </GridCell>

              <GridCell columnSpan={2}>
                <FormField
                  id="DEVID"
                  classNames={textInputBoxClass}
                  label="DEV ID"
                  required={true}
                  inputFactory={(id, name, onValidation) => (
                    <MaskedTextInput
                      id={id}
                      name={name}
                      value={userInput?.tevalis?.DEVID}
                      placeholder="Enter DEVID"
                      onValidation={onValidation}
                      onChange={(_, n) =>
                        setUserInput((prev) => ({
                          ...prev,
                          tevalis: { ...prev.tevalis, DEVID: n },
                        }))
                      }
                    />
                  )}
                />
              </GridCell>

              <GridCell columnSpan={2}>
                <FormField
                  id="GUID"
                  classNames={textInputBoxClass}
                  label="GUID"
                  required={true}
                  inputFactory={(id, name, onValidation) => (
                    <MaskedTextInput
                      id={id}
                      name={name}
                      value={userInput?.tevalis?.GUID}
                      placeholder="Enter GUID"
                      onValidation={onValidation}
                      onChange={(_, n) =>
                        setUserInput((prev) => ({ ...prev, tevalis: { ...prev.tevalis, GUID: n } }))
                      }
                    />
                  )}
                />
              </GridCell>

              <GridCell columnSpan={2}>
                <FormField
                  id="GUID2"
                  classNames={textInputBoxClass}
                  label="GUID 2"
                  required={true}
                  inputFactory={(id, name, onValidation) => (
                    <MaskedTextInput
                      id={id}
                      name={name}
                      value={userInput?.tevalis?.GUID2}
                      placeholder="Enter GUID 2"
                      onValidation={onValidation}
                      onChange={(_, n) =>
                        setUserInput((prev) => ({
                          ...prev,
                          tevalis: { ...prev.tevalis, GUID2: n },
                        }))
                      }
                    />
                  )}
                />
              </GridCell>

              <GridCell columnSpan={2}>
                <FormField
                  id="siteId"
                  classNames={textInputBoxClass}
                  label="Site Id"
                  required={true}
                  inputFactory={(id, name, onValidation) => (
                    <MaskedTextInput
                      id={id}
                      name={name}
                      value={userInput?.tevalis?.siteId}
                      placeholder="Enter Site Id"
                      onValidation={onValidation}
                      onChange={(_, n) =>
                        setUserInput((prev) => ({
                          ...prev,
                          tevalis: { ...prev.tevalis, siteId: n },
                        }))
                      }
                    />
                  )}
                />
              </GridCell>
            </>
          )}

          {/* PAYMENT PROVIDER */}
          <GridCell columnSpan={4} classNames={["mt-6"]}>
            <PageTitle title="Payment Provider" />
          </GridCell>

          <GridCell columnSpan={4}>
            <FormField
              id="paymentProvider"
              classNames={["mb-5"]}
              inputFactory={() => (
                <InRow gap={3} classNames={["mt-2"]}>
                  {["stripe", "square", "paystack", "cybersource"].map((provider) => {
                    const isLockedBySquarePos = userInput?.squareSync && provider !== "square";
                    return (
                      <label
                        key={provider}
                        className={`flex items-center gap-2 ${isLockedBySquarePos ? "cursor-not-allowed opacity-50" : "cursor-pointer"}`}>
                        <input
                          type="radio"
                          name="paymentProvider"
                          value={provider}
                          checked={
                            userInput?.paymentProvider
                              ? userInput?.paymentProvider === provider
                              : provider === "stripe"
                          }
                          disabled={isLockedBySquarePos}
                          onChange={(e) =>
                            setUserInput((prev) => ({ ...prev, paymentProvider: e.target.value }))
                          }
                          className="peer hidden"
                        />
                        <span className="w-5 h-5 rounded-full border-2 border-gray-400 relative peer-checked:border-secondary-primary_button_bg after:content-[''] after:w-2.5 after:h-2.5 after:rounded-full after:bg-secondary-primary_button_bg after:absolute after:top-1/2 after:left-1/2 after:-translate-x-1/2 after:-translate-y-1/2 after:hidden peer-checked:after:block"></span>
                        <span className="text-sm font-medium capitalize">{provider}</span>
                      </label>
                    );
                  })}
                </InRow>
              )}
            />
          </GridCell>

          {selectedProvider === "stripe" && (
            <>
              {/* 5.1️⃣ STRIPE */}
              <GridCell columnSpan={2}>
                <FormField
                  id="connectedAccountId"
                  classNames={textInputBoxClass}
                  label="Connected Account Id"
                  required={true}
                  inputFactory={(id, name, onValidation) => (
                    <MaskedTextInput
                      id={id}
                      name={name}
                      value={userInput?.stripeConfig?.connectedAccountId}
                      placeholder="Enter Connected Account Id"
                      onValidation={onValidation}
                      onChange={(_, n) =>
                        setUserInput((prev) => ({
                          ...prev,
                          stripeConfig: { ...prev.stripeConfig, connectedAccountId: n },
                        }))
                      }
                    />
                  )}
                />
              </GridCell>

              <GridCell columnSpan={2}>
                <FormField
                  id="country"
                  label="Country"
                  required={true}
                  inputFactory={(id, name, onValidation) => (
                    <SelectInput
                      disabled={false}
                      id={id}
                      name={name}
                      isCopyable={true}
                      placeholder="Select Country"
                      selectedKey={userInput?.stripeConfig?.country?.toLowerCase()}
                      optionProvider={() => countries}
                      keySelector={(x) => `${x.value}`}
                      labelSelector={(x) => `${x.label}`}
                      onValidation={onValidation}
                      onChange={(_, n, selector) =>
                        handleCountryChange("stripeConfig", n, selector)
                      }
                    />
                  )}
                />
              </GridCell>

              <GridCell columnSpan={2}>
                <FormField
                  id="feeMoney"
                  classNames={textInputBoxClass}
                  label="Fee Money"
                  required={true}
                  inputFactory={(id, name, onValidation) => (
                    <TextInput
                      id={id}
                      name={name}
                      isCopyable={true}
                      type="number"
                      min={0}
                      value={String(userInput?.stripeConfig?.feeMoney)}
                      placeholder="Enter Fee Money"
                      onValidation={onValidation}
                      onChange={(_, n) =>
                        setUserInput((prev) => ({
                          ...prev,
                          stripeConfig: { ...prev.stripeConfig, feeMoney: parseFloat(n) || 0 },
                        }))
                      }
                    />
                  )}
                />
              </GridCell>

              <GridCell columnSpan={2}>
                <FormField
                  id="processingFeePercentage"
                  classNames={textInputBoxClass}
                  label="Processing Fee Percentage"
                  required={userInput?.stripeConfig?.isProcessingFeeEnabled}
                  inputFactory={(id, name, onValidation) => (
                    <TextInput
                      id={id}
                      name={name}
                      isCopyable={true}
                      type="number"
                      min={0}
                      value={String(userInput?.stripeConfig?.processingFeePercentage)}
                      placeholder="Enter Percentage"
                      onValidation={onValidation}
                      onChange={(_, n) =>
                        setUserInput((prev) => ({
                          ...prev,
                          stripeConfig: {
                            ...prev.stripeConfig,
                            processingFeePercentage: parseFloat(n) || 0,
                          },
                        }))
                      }
                    />
                  )}
                />
              </GridCell>

              <GridCell columnSpan={2}>
                <FormField
                  id="publishableKey"
                  classNames={textInputBoxClass}
                  label="Publishable Key"
                  required={true}
                  inputFactory={(id, name, onValidation) => (
                    <TextInput
                      id={id}
                      name={name}
                      isCopyable={true}
                      value={userInput?.stripeConfig?.publishableKey}
                      placeholder="Enter Publishable Key"
                      onValidation={onValidation}
                      onChange={(_, n) =>
                        setUserInput((prev) => ({
                          ...prev,
                          stripeConfig: { ...prev.stripeConfig, publishableKey: n },
                        }))
                      }
                    />
                  )}
                />
              </GridCell>

              <GridCell columnSpan={2}>
                <FormField
                  id="secretKey"
                  classNames={textInputBoxClass}
                  label="Secret Key"
                  required={true}
                  inputFactory={(id, name, onValidation) => (
                    <MaskedTextInput
                      id={id}
                      name={name}
                      value={userInput?.stripeConfig?.secretKey}
                      placeholder="Enter Secret Key"
                      onValidation={onValidation}
                      onChange={(_, n) =>
                        setUserInput((prev) => ({
                          ...prev,
                          stripeConfig: { ...prev.stripeConfig, secretKey: n },
                        }))
                      }
                    />
                  )}
                />
              </GridCell>

              <GridCell columnSpan={2}>
                <FormField
                  id="taxCode"
                  classNames={textInputBoxClass}
                  label="Tax Code"
                  required={true}
                  inputFactory={(id, name, onValidation) => (
                    <TextInput
                      id={id}
                      name={name}
                      isCopyable={true}
                      value={userInput?.stripeConfig?.taxCode}
                      placeholder="Enter Tax Code"
                      onValidation={onValidation}
                      onChange={(_, n) =>
                        setUserInput((prev) => ({
                          ...prev,
                          stripeConfig: { ...prev.stripeConfig, taxCode: n },
                        }))
                      }
                    />
                  )}
                />
              </GridCell>

              <GridCell columnSpan={2}></GridCell>

              <GridCell columnSpan={2}>
                <FormField
                  id="isProcessingFeeEnabled"
                  classNames={["text_input_box h-full flex justify-end mt-2"]}
                  inputFactory={() => (
                    <>
                      <InRow gap={2} classNames={["w-full !justify-between mb-2"]}>
                        <label className="m-0">Is Processing Fee Enabled</label>
                        <ToggleButton
                          checked={userInput?.stripeConfig?.isProcessingFeeEnabled}
                          onChange={(checked) =>
                            setUserInput((prev) => ({
                              ...prev,
                              stripeConfig: {
                                ...prev.stripeConfig,
                                isProcessingFeeEnabled: checked,
                              },
                            }))
                          }
                        />
                      </InRow>
                    </>
                  )}
                />
              </GridCell>
            </>
          )}

          {selectedProvider === "paystack" && (
            <>
              {/* 5.2️⃣ PAYSTACK */}
              <GridCell columnSpan={2}>
                <FormField
                  id="apiUrl"
                  classNames={textInputBoxClass}
                  label="API URL"
                  required={true}
                  inputFactory={(id, name, onValidation) => (
                    <TextInput
                      id={id}
                      name={name}
                      isCopyable={true}
                      value={userInput?.payStack?.apiUrl}
                      placeholder="Enter API URL"
                      onValidation={onValidation}
                      onChange={(_, n) =>
                        setUserInput((prev) => ({
                          ...prev,
                          payStack: { ...prev.payStack, apiUrl: n },
                        }))
                      }
                    />
                  )}
                />
              </GridCell>

              <GridCell columnSpan={2}>
                <FormField
                  id="feeMoney"
                  classNames={textInputBoxClass}
                  label="Fee Money"
                  required={true}
                  inputFactory={(id, name, onValidation) => (
                    <TextInput
                      id={id}
                      name={name}
                      isCopyable={true}
                      type="number"
                      min={0}
                      value={String(userInput?.payStack?.feeMoney)}
                      placeholder="Enter Fee Money"
                      onValidation={onValidation}
                      onChange={(_, n) =>
                        setUserInput((prev) => ({
                          ...prev,
                          payStack: { ...prev.payStack, feeMoney: parseFloat(n) || 0 },
                        }))
                      }
                    />
                  )}
                />
              </GridCell>

              <GridCell columnSpan={2}>
                <FormField
                  id="secretKey"
                  classNames={textInputBoxClass}
                  label="Secret Key"
                  required={true}
                  inputFactory={(id, name, onValidation) => (
                    <MaskedTextInput
                      id={id}
                      name={name}
                      value={userInput?.payStack?.secretKey}
                      placeholder="Enter Secret Key"
                      onValidation={onValidation}
                      onChange={(_, n) =>
                        setUserInput((prev) => ({
                          ...prev,
                          payStack: { ...prev.payStack, secretKey: n },
                        }))
                      }
                    />
                  )}
                />
              </GridCell>

              <GridCell columnSpan={2}>
                <FormField
                  id="subAccount"
                  classNames={textInputBoxClass}
                  label="Sub Account"
                  required={true}
                  inputFactory={(id, name, onValidation) => (
                    <MaskedTextInput
                      id={id}
                      name={name}
                      value={userInput?.payStack?.subAccount}
                      placeholder="Enter Sub Account"
                      onValidation={onValidation}
                      onChange={(_, n) =>
                        setUserInput((prev) => ({
                          ...prev,
                          payStack: { ...prev.payStack, subAccount: n },
                        }))
                      }
                    />
                  )}
                />
              </GridCell>
            </>
          )}

          {selectedProvider === "cybersource" && (
            <>
              <GridCell columnSpan={2}>
                <FormField
                  id="apiKeyId"
                  classNames={textInputBoxClass}
                  label="API Key Id"
                  required={true}
                  inputFactory={(id, name, onValidation) => (
                    <MaskedTextInput
                      id={id}
                      name={name}
                      value={userInput?.cyberSourceConfig?.apiKeyId}
                      placeholder="Enter API Key ID"
                      onValidation={onValidation}
                      onChange={(_, n) =>
                        setUserInput((prev) => ({
                          ...prev,
                          cyberSourceConfig: { ...prev.cyberSourceConfig, apiKeyId: n },
                        }))
                      }
                    />
                  )}
                />
              </GridCell>

              <GridCell columnSpan={2}>
                <FormField
                  id="cyberSourceCurrency"
                  classNames={textInputBoxClass}
                  label="Currency"
                  required={true}
                  inputFactory={(id, name, onValidation) => (
                    <TextInput
                      id={id}
                      name={name}
                      isCopyable={true}
                      value={userInput?.cyberSourceConfig?.currency}
                      placeholder="Enter Currency (e.g. AUD)"
                      onValidation={onValidation}
                      onChange={(_, n) =>
                        setUserInput((prev) => ({
                          ...prev,
                          cyberSourceConfig: { ...prev.cyberSourceConfig, currency: n },
                        }))
                      }
                    />
                  )}
                />
              </GridCell>

              <GridCell columnSpan={2}>
                <FormField
                  id="cyberSourceEnvironment"
                  classNames={textInputBoxClass}
                  label="Environment"
                  required={true}
                  inputFactory={(id, name, onValidation) => (
                    <TextInput
                      id={id}
                      name={name}
                      isCopyable={true}
                      value={userInput?.cyberSourceConfig?.environment}
                      placeholder="Enter Environment (e.g. api.cybersource.com)"
                      onValidation={onValidation}
                      onChange={(_, n) =>
                        setUserInput((prev) => ({
                          ...prev,
                          cyberSourceConfig: { ...prev.cyberSourceConfig, environment: n },
                        }))
                      }
                    />
                  )}
                />
              </GridCell>

              <GridCell columnSpan={2}>
                <FormField
                  id="merchantId"
                  classNames={textInputBoxClass}
                  label="Merchant Id"
                  required={true}
                  inputFactory={(id, name, onValidation) => (
                    <MaskedTextInput
                      id={id}
                      name={name}
                      value={userInput?.cyberSourceConfig?.merchantId}
                      placeholder="Enter Merchant ID"
                      onValidation={onValidation}
                      onChange={(_, n) =>
                        setUserInput((prev) => ({
                          ...prev,
                          cyberSourceConfig: { ...prev.cyberSourceConfig, merchantId: n },
                        }))
                      }
                    />
                  )}
                />
              </GridCell>

              <GridCell columnSpan={2}>
                <FormField
                  id="secretKey"
                  classNames={textInputBoxClass}
                  label="Secret Key"
                  required={true}
                  inputFactory={(id, name, onValidation) => (
                    <MaskedTextInput
                      id={id}
                      name={name}
                      value={userInput?.cyberSourceConfig?.secretKey}
                      placeholder="Enter Secret Key"
                      onValidation={onValidation}
                      onChange={(_, n) =>
                        setUserInput((prev) => ({
                          ...prev,
                          cyberSourceConfig: { ...prev.cyberSourceConfig, secretKey: n },
                        }))
                      }
                    />
                  )}
                />
              </GridCell>
            </>
          )}

          {selectedProvider === "square" && (
            <>
              {/* 5.3️⃣ SQUARE */}
              <GridCell columnSpan={2}>
                <FormField
                  id="accessToken"
                  classNames={textInputBoxClass}
                  label="Access Token"
                  required={true}
                  inputFactory={(id, name, onValidation) => (
                    <MaskedTextInput
                      id={id}
                      name={name}
                      value={userInput?.squareConfig?.accessToken}
                      placeholder="Enter Access Token"
                      onValidation={onValidation}
                      onChange={(_, n) =>
                        setUserInput((prev) => ({
                          ...prev,
                          squareConfig: { ...prev.squareConfig, accessToken: n },
                        }))
                      }
                    />
                  )}
                />
              </GridCell>

              <GridCell columnSpan={2}>
                <FormField
                  id="appFeePercentage"
                  classNames={textInputBoxClass}
                  label="App Fee Percentage"
                  required={true}
                  inputFactory={(id, name, onValidation) => (
                    <TextInput
                      id={id}
                      name={name}
                      isCopyable={true}
                      type="number"
                      min={0}
                      value={String(userInput?.squareConfig?.appFeePercentage)}
                      placeholder="Enter App Fee Percentage"
                      onValidation={onValidation}
                      onChange={(_, n) =>
                        setUserInput((prev) => ({
                          ...prev,
                          squareConfig: {
                            ...prev.squareConfig,
                            appFeePercentage: parseFloat(n) || 0,
                          },
                        }))
                      }
                    />
                  )}
                />
              </GridCell>

              <GridCell columnSpan={2}>
                <FormField
                  id="appId"
                  classNames={textInputBoxClass}
                  label="App Id"
                  required={true}
                  inputFactory={(id, name, onValidation) => (
                    <TextInput
                      id={id}
                      name={name}
                      isCopyable={true}
                      value={userInput?.squareConfig?.appId}
                      placeholder="Enter App Id"
                      onValidation={onValidation}
                      onChange={(_, n) =>
                        setUserInput((prev) => ({
                          ...prev,
                          squareConfig: { ...prev.squareConfig, appId: n },
                        }))
                      }
                    />
                  )}
                />
              </GridCell>

              <GridCell columnSpan={2}>
                <FormField
                  id="appSecret"
                  classNames={textInputBoxClass}
                  label="App Secret"
                  required={true}
                  inputFactory={(id, name, onValidation) => (
                    <MaskedTextInput
                      id={id}
                      name={name}
                      value={userInput?.squareConfig?.appSecret}
                      placeholder="Enter App Secret"
                      onValidation={onValidation}
                      onChange={(_, n) =>
                        setUserInput((prev) => ({
                          ...prev,
                          squareConfig: { ...prev.squareConfig, appSecret: n },
                        }))
                      }
                    />
                  )}
                />
              </GridCell>

              <GridCell columnSpan={2}>
                <FormField
                  id="basePath"
                  classNames={textInputBoxClass}
                  label="Base Path"
                  required={true}
                  inputFactory={(id, name, onValidation) => (
                    <TextInput
                      id={id}
                      name={name}
                      isCopyable={true}
                      type="url"
                      value={userInput?.squareConfig?.basePath}
                      placeholder="Enter Base Path"
                      onValidation={onValidation}
                      onChange={(_, n) =>
                        setUserInput((prev) => ({
                          ...prev,
                          squareConfig: { ...prev.squareConfig, basePath: n },
                        }))
                      }
                    />
                  )}
                />
              </GridCell>

              <GridCell columnSpan={2}>
                <FormField
                  id="country"
                  classNames={textInputBoxClass}
                  label="Country"
                  required={true}
                  inputFactory={(id, name, onValidation) => (
                    <SelectInput
                      disabled={false}
                      id={id}
                      name={name}
                      isCopyable={true}
                      placeholder="Select Country"
                      selectedKey={userInput?.squareConfig?.country?.toLowerCase()}
                      optionProvider={() => countries}
                      keySelector={(x) => `${x.value}`}
                      labelSelector={(x) => `${x.label}`}
                      onChange={(_, n, selector) =>
                        handleCountryChange("squareConfig", n, selector)
                      }
                    />
                  )}
                />
              </GridCell>

              <GridCell columnSpan={2}>
                <FormField
                  id="environment"
                  classNames={textInputBoxClass}
                  label="Environment"
                  required={true}
                  inputFactory={(id, name, onValidation) => (
                    <TextInput
                      id={id}
                      name={name}
                      isCopyable={true}
                      value={userInput?.squareConfig?.environment}
                      placeholder="sandbox | production"
                      onValidation={onValidation}
                      onChange={(_, n) =>
                        setUserInput((prev) => ({
                          ...prev,
                          squareConfig: { ...prev.squareConfig, environment: n },
                        }))
                      }
                    />
                  )}
                />
              </GridCell>

              <GridCell columnSpan={2}>
                <FormField
                  id="expiresAt"
                  classNames={textInputBoxClass}
                  label="Expires At"
                  required={true}
                  inputFactory={(id, name, onValidation) => (
                    <TextInput
                      id={id}
                      name={name}
                      isCopyable={true}
                      value={userInput?.squareConfig?.expiresAt}
                      placeholder="Enter Expiry"
                      onValidation={onValidation}
                      onChange={(_, n) =>
                        setUserInput((prev) => ({
                          ...prev,
                          squareConfig: { ...prev.squareConfig, expiresAt: n },
                        }))
                      }
                    />
                  )}
                />
              </GridCell>

              <GridCell columnSpan={2}>
                <FormField
                  id="locationId"
                  classNames={textInputBoxClass}
                  label="Location Id"
                  required={true}
                  inputFactory={(id, name, onValidation) => (
                    <MaskedTextInput
                      id={id}
                      name={name}
                      value={userInput?.squareConfig?.locationId}
                      placeholder="Enter Location Id"
                      onValidation={onValidation}
                      onChange={(_, n) =>
                        setUserInput((prev) => ({
                          ...prev,
                          squareConfig: { ...prev.squareConfig, locationId: n },
                        }))
                      }
                    />
                  )}
                />
              </GridCell>

              <GridCell columnSpan={2}>
                <FormField
                  id="merchantId"
                  classNames={textInputBoxClass}
                  label="Merchant Id"
                  required={true}
                  inputFactory={(id, name, onValidation) => (
                    <MaskedTextInput
                      id={id}
                      name={name}
                      value={userInput?.squareConfig?.merchantId}
                      placeholder="Enter Merchant Id"
                      onValidation={onValidation}
                      onChange={(_, n) =>
                        setUserInput((prev) => ({
                          ...prev,
                          squareConfig: { ...prev.squareConfig, merchantId: n },
                        }))
                      }
                    />
                  )}
                />
              </GridCell>

              <GridCell columnSpan={2}>
                <FormField
                  id="redirectUri"
                  classNames={textInputBoxClass}
                  label="Redirect URI"
                  required={true}
                  inputFactory={(id, name, onValidation) => (
                    <TextInput
                      id={id}
                      name={name}
                      isCopyable={true}
                      value={userInput?.squareConfig?.redirectUri}
                      placeholder="Enter Redirect URI"
                      onValidation={onValidation}
                      onChange={(_, n) =>
                        setUserInput((prev) => ({
                          ...prev,
                          squareConfig: { ...prev.squareConfig, redirectUri: n },
                        }))
                      }
                    />
                  )}
                />
              </GridCell>

              <GridCell columnSpan={2}>
                <FormField
                  id="refreshToken"
                  classNames={textInputBoxClass}
                  label="Refresh Token"
                  required={true}
                  inputFactory={(id, name, onValidation) => (
                    <MaskedTextInput
                      id={id}
                      name={name}
                      value={userInput?.squareConfig?.refreshToken}
                      placeholder="Enter Refresh Token"
                      onValidation={onValidation}
                      onChange={(_, n) =>
                        setUserInput((prev) => ({
                          ...prev,
                          squareConfig: { ...prev.squareConfig, refreshToken: n },
                        }))
                      }
                    />
                  )}
                />
              </GridCell>

              <GridCell columnSpan={2}>
                <FormField
                  id="appFeeAllowed"
                  classNames={["text_input_box h-full flex justify-end mt-2"]}
                  inputFactory={() => (
                    <>
                      <InRow gap={2} classNames={["w-full !justify-between mb-2"]}>
                        <label className="m-0">App Fee Allowed</label>
                        <ToggleButton
                          checked={userInput?.squareConfig?.appFeeAllowed}
                          onChange={(checked) =>
                            setUserInput((prev) => ({
                              ...prev,
                              squareConfig: { ...prev.squareConfig, appFeeAllowed: checked },
                            }))
                          }
                        />
                      </InRow>
                    </>
                  )}
                />
              </GridCell>

              <GridCell columnSpan={2}>
                <FormField
                  id="isRestaurantMenu"
                  classNames={["text_input_box h-full flex justify-end mt-2"]}
                  inputFactory={() => (
                    <InRow gap={2} classNames={["w-full !justify-between mb-2"]}>
                      <label className="m-0">Is Restaurant Menu</label>
                      <ToggleButton
                        checked={userInput?.squareConfig?.isRestaurantMenu}
                        onChange={(checked) =>
                          setUserInput((prev) => ({
                            ...prev,
                            squareConfig: { ...prev.squareConfig, isRestaurantMenu: checked },
                          }))
                        }
                      />
                    </InRow>
                  )}
                />
              </GridCell>
            </>
          )}
        </Grid>
        <InRow
          gap={1}
          classNames={[
            "mt-10 bg-blue-50 p-3.5 rounded-md border border-blue-300 text-sm opacity-80",
          ]}>
          <IoInformationCircleOutline size={22} />
          <span>
            <span className="font-bold">Note:</span> It may take up to{" "}
            <span className="font-bold">1 hour</span> for these changes to take effect and be
            reflected across the system.
          </span>
        </InRow>
        <InRow gap={1} classNames={["justify-center mt-8"]}>
          <PrimaryButton classNames={["min-w-[130px]"]} action={handleSubmit}>
            Submit
          </PrimaryButton>
          <SecondaryButton classNames={["min-w-[130px]"]} action={onCancel}>
            Cancel
          </SecondaryButton>
        </InRow>
      </Form>
    </FormErrorContext.Provider>
  );
};
